import { useTranslation } from 'react-i18next'
import { LocalizedLink } from '@/components/LocalizedLink'
import { ArrowRight, Shield, Leaf, Globe, TreeDeciduous, Star } from 'lucide-react'
import { motion, useInView } from 'framer-motion'
import { useRef, useEffect, useState } from 'react'

const ease = [0.16, 1, 0.3, 1] as [number, number, number, number]

function AnimatedCounter({ end, suffix = '', duration = 2000 }: { end: number; suffix?: string; duration?: number }) {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, amount: 0.5 })
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!isInView) return
    let start = 0
    const step = Math.ceil(end / (duration / 16))
    const timer = setInterval(() => {
      start += step
      if (start >= end) {
        setCount(end)
        clearInterval(timer)
      } else {
        setCount(start)
      }
    }, 16)
    return () => clearInterval(timer)
  }, [isInView, end, duration])

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  )
}

const productCategories = [
  { key: 'panettoni', image: '/images/products/panettoni-thumb.png' },
  { key: 'colombe', image: '/images/products/colombe-thumb.png' },
  { key: 'pasticceria', image: '/images/products/pasticceria-thumb.png' },
]

const flagshipProducts = [
  { key: 'panettone', image: '/images/products/panettone-classico.png' },
  { key: 'colomba', image: '/images/products/colomba-classica.png' },
  { key: 'torta', image: '/images/products/torta-nocciole.png' },
]

const certifications = [
  { key: 'brc', icon: Shield },
  { key: 'fairtrade', icon: Globe },
  { key: 'ecovadis', icon: Leaf },
  { key: 'rainforest', icon: TreeDeciduous },
]

const stats = [
  { key: 'years', value: 90, suffix: '+' },
  { key: 'products', value: 100, suffix: '+' },
  { key: 'countries', value: 23, suffix: '+' },
  { key: 'stars', value: 3, suffix: '' },
]

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.7, ease },
  }),
}

export function HomePage() {
  const { t } = useTranslation('common')

  return (
    <>
      {/* 1. Brand Introduction */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease }}
            className="w-16 h-0.5 bg-brand mx-auto mb-10 origin-center"
          />
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.2, ease }}
            className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-neutral-900 leading-snug mb-6"
          >
            {t('home.intro.tagline')}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.4, ease }}
            className="text-lg md:text-xl text-neutral-600 max-w-2xl mx-auto"
          >
            {t('home.intro.subtitle')}
          </motion.p>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.6, ease }}
            className="w-16 h-0.5 bg-brand mx-auto mt-10 origin-center"
          />
        </div>
      </section>

      {/* 2. Products Showcase */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease }}
            className="text-center mb-14"
          >
            <span className="inline-flex items-center gap-3 text-brand font-heading font-medium tracking-wider uppercase text-sm mb-4">
              <span className="w-8 h-px bg-brand" />
              {t('home.products.label')}
              <span className="w-8 h-px bg-brand" />
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900 mb-4">
              {t('home.products.title')}
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              {t('home.products.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {productCategories.map((category, i) => (
              <motion.div
                key={category.key}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
              >
                <LocalizedLink to={`products?category=${category.key}`} className="group block">
                  <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-gradient-to-b from-brand-light to-neutral-100">
                    <img
                      src={category.image}
                      alt={t(`home.products.${category.key}.title`)}
                      className="absolute inset-0 w-full h-full object-contain p-8 transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/80 via-neutral-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                      <h3 className="text-xl font-heading font-semibold text-white mb-1">
                        {t(`home.products.${category.key}.title`)}
                      </h3>
                      <span className="inline-flex items-center gap-1 text-brand text-sm font-medium">
                        {t('home.products.explore')}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </LocalizedLink>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Heritage / Sourdough Story */}
      <section className="bg-neutral-900 text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left: Images */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, ease }}
              className="relative"
            >
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden">
                <img
                  src="/images/choice/lievito-madre.jpg"
                  alt="Lievito madre"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-brand/5 to-transparent pointer-events-none" />
              </div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.6, ease }}
                className="absolute top-6 right-4 md:top-8 md:right-2 bg-brand text-neutral-900 px-4 py-2 rounded-full font-heading font-bold text-sm shadow-lg"
              >
                {t('home.heritage.badge')}
              </motion.div>
            </motion.div>

            {/* Right: Text */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, ease }}
            >
              <span className="inline-flex items-center gap-2 text-brand font-heading font-medium tracking-wider uppercase text-sm mb-6">
                <span className="w-8 h-px bg-brand" />
                {t('home.heritage.label')}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold leading-tight mb-6 text-white">
                {t('home.heritage.title')}
              </h2>
              <p className="text-lg text-neutral-400 mb-10 leading-relaxed">
                {t('home.heritage.description')}
              </p>
              <LocalizedLink
                to="company"
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand text-neutral-900 font-heading font-semibold rounded-full hover:bg-brand-dark transition-colors"
              >
                {t('home.heritage.cta')}
                <ArrowRight className="w-4 h-4" />
              </LocalizedLink>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. Numbers / Social Proof */}
      <section className="bg-brand-light py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: i * 0.1, duration: 0.6, ease }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-neutral-900 mb-2">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <p className="text-sm md:text-base text-neutral-700 font-medium uppercase tracking-wider">
                  {t(`home.numbers.${stat.key}`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Flagship Products */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease }}
            className="text-center mb-14"
          >
            <span className="inline-flex items-center gap-3 text-brand font-heading font-medium tracking-wider uppercase text-sm mb-4">
              <span className="w-8 h-px bg-brand" />
              {t('home.flagship.label')}
              <span className="w-8 h-px bg-brand" />
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900">
              {t('home.flagship.title')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {flagshipProducts.map((product, i) => (
              <motion.div
                key={product.key}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="text-center"
              >
                <motion.div
                  whileHover={{ y: -10, rotate: 2 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  className="relative w-56 h-56 md:w-64 md:h-64 mx-auto mb-6"
                >
                  <div className="absolute inset-0 rounded-full bg-brand-light/50" />
                  <img
                    src={product.image}
                    alt={t(`home.flagship.${product.key}.name`)}
                    className="relative w-full h-full object-contain drop-shadow-xl p-4"
                  />
                </motion.div>
                <h3 className="text-xl font-heading font-semibold text-neutral-900 mb-2">
                  {t(`home.flagship.${product.key}.name`)}
                </h3>
                <p className="text-neutral-600">
                  {t(`home.flagship.${product.key}.description`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Certifications & Trust */}
      <section className="section-padding bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease }}
            className="text-center mb-14"
          >
            <span className="inline-flex items-center gap-3 text-brand font-heading font-medium tracking-wider uppercase text-sm mb-4">
              <span className="w-8 h-px bg-brand" />
              {t('home.trust.label')}
              <span className="w-8 h-px bg-brand" />
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900">
              {t('home.trust.title')}
            </h2>
          </motion.div>

          {/* Award Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease }}
            className="max-w-xl mx-auto bg-white rounded-2xl p-8 md:p-10 shadow-sm text-center mb-12"
          >
            <div className="flex justify-center gap-2 mb-6">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0, rotate: -180 }}
                  whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    delay: 0.3 + i * 0.15,
                    type: 'spring',
                    stiffness: 200,
                    damping: 12,
                  }}
                >
                  <Star className="w-8 h-8 text-brand fill-brand" />
                </motion.div>
              ))}
            </div>
            <p className="text-lg text-neutral-700 font-medium">
              {t('home.trust.award')}
            </p>
          </motion.div>

          {/* Certification Icons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.2, ease }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto mb-10"
          >
            {certifications.map((cert) => (
              <div key={cert.key} className="flex flex-col items-center gap-3 p-4">
                <div className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center">
                  <cert.icon className="w-6 h-6 text-neutral-700" />
                </div>
                <span className="text-sm text-neutral-600 font-medium text-center">
                  {t(`home.trust.${cert.key}`)}
                </span>
              </div>
            ))}
          </motion.div>

          <div className="text-center">
            <LocalizedLink
              to="sustainability"
              className="inline-flex items-center gap-2 px-6 py-3 bg-neutral-900 text-white font-heading font-medium rounded-full hover:bg-neutral-800 transition-colors"
            >
              {t('home.trust.cta')}
              <ArrowRight className="w-4 h-4" />
            </LocalizedLink>
          </div>
        </div>
      </section>

    </>
  )
}
